import { effectiveDeploymentAddress } from '@/features/onboarding/deploymentAddress';
import { workAreaDataSourceRegistry } from '@/data-sources/WorkAreaDataSourceRegistry';
import { createBrowserLocalDataDriver, localSyncScope } from './browserStorageDriver';
import { LocalSyncStore, localSyncStore } from './localSyncStore';

/**
 * FR-11 (I-1588): a work area's cached data lives in the local copy of the connection that holds
 * it, not in the copy of the deployment the app was started on.
 *
 * The copy is named by a scope (FR-9: `featit-local-sync:<scope>`, `featit:local-sync@<scope>:…`).
 * A work area bound to a custom connection (I-1120) has its own scope, so its rows never sit beside
 * another deployment's, and the owner rule that drops a different person's copies finds it by the
 * same prefixes. A work area on the main connection gets the start-up store, so no installation
 * loses its copy.
 *
 * ⛔ A work area's data is cached by asking for the store of that work area, never through the
 * `localSyncStore` singleton, which is the start-up deployment's alone
 * (`localSyncReach.guard.test.ts` lists what may still import it).
 */
export interface ILocalSyncStoreRegistryDependencies {
  /**
   * The scope of the copy that holds `workAreaId`: a string for a work area on a custom connection,
   * `null` for one on the built-in deployment, `undefined` for one on the start-up deployment.
   */
  scopeOf(workAreaId: string): string | null | undefined;
  /** The scope the start-up store was built with, so a connection at that address shares it. */
  startUpScope(): string | null;
  createStore(scope: string | null): LocalSyncStore;
}

export class LocalSyncStoreRegistry {
  private readonly stores = new Map<string, LocalSyncStore>();

  constructor(
    private readonly dependencies: ILocalSyncStoreRegistryDependencies = {
      scopeOf: customConnectionScopeOf,
      startUpScope: () => localSyncScope(effectiveDeploymentAddress()),
      createStore: (scope) => new LocalSyncStore(createBrowserLocalDataDriver(scope)),
    },
    private readonly startUpStore: LocalSyncStore = localSyncStore,
  ) {}

  storeFor(workAreaId: string | null | undefined): LocalSyncStore {
    const scope = workAreaId ? this.dependencies.scopeOf(workAreaId) : undefined;
    if (scope === undefined || scope === this.dependencies.startUpScope()) return this.startUpStore;
    const key = scope ?? '';
    let store = this.stores.get(key);
    if (!store) {
      store = this.dependencies.createStore(scope);
      this.stores.set(key, store);
    }
    return store;
  }
}

/**
 * The scope of the connection a work area is bound to on the desktop (ADR-153), or `undefined` for
 * a work area on the deployment the app is signed into. A connection with no recorded address is
 * named by its id, so two such connections still do not share a copy.
 */
function customConnectionScopeOf(workAreaId: string): string | null | undefined {
  const binding = workAreaDataSourceRegistry.descriptor(workAreaId)?.desktopBinding;
  if (!binding?.connectionId) return undefined;
  return binding.apiBaseUrl
    ? localSyncScope(binding.apiBaseUrl)
    : `connection-${binding.connectionId.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
}

export const localSyncStores = new LocalSyncStoreRegistry();

/** The local store for the connection that holds `workAreaId` (FR-11). */
export function localSyncStoreFor(workAreaId: string | null | undefined): LocalSyncStore {
  return localSyncStores.storeFor(workAreaId);
}
